import React from "react"

import {
  ANONYMOUS_SESSION,
  createLeaveGuard,
  derived,
  writable,
  type Entry,
  type Flash,
  type Host,
  type Visit,
  type VisitRequest,
} from "./host"
import { HostProvider, useEnclosingHost, useReadable } from "./host-react"
import {
  FetchPage,
  FetchPageOperation,
  Page,
  PageSink,
  Scope,
  VisitMethod,
} from "./types"

// Renders one page and navigates from it: a child layer embedded in the
// document, providing the pages it renders the same host binding the root
// does. A driver with a `scope` renders only the resolved pages the scope
// accepts and hands every other page to its sink: the `host` prop when
// given, else the enclosing host's `receive`. Only the root driver may have
// neither, so a scoped driver with no sink is a composition error and fails
// at mount. A driver with no scope renders every page it resolves, as an
// embedding with no host of its own must.
export function Driver({
  initialPage,
  fetchPage,
  scope,
  host: sink,
}: {
  initialPage: Page
  fetchPage: FetchPage
  scope?: Scope
  host?: PageSink
}) {
  const enclosing = useEnclosingHost()
  const receiveDeclined = sink ?? enclosing?.receive ?? null
  if (scope && !receiveDeclined) {
    throw new Error(
      "[page-rendering] a scoped Driver needs a host sink or an enclosing driver",
    )
  }

  const [layer] = React.useState(() =>
    createDriverLayer(initialPage, enclosing),
  )
  React.useEffect(() => () => layer.cancel(), [layer])
  layer.configure({ fetchPage, scope, sink: receiveDeclined })

  const { node } = useReadable(layer.page)

  return <HostProvider host={layer.host}>{node}</HostProvider>
}

type DriverLayer = {
  host: Host
  page: { get(): Page; subscribe(onChange: () => void): () => void }
  configure(options: {
    fetchPage: FetchPage
    scope: Scope | undefined
    sink: PageSink | null
  }): void
  cancel(): void
}

// The visit a resolved page is the outcome of. The host shares the flash
// among the page's props, as it does the path parameters entryOf reads, so
// a page a driver fetched after a redirect shows the flash that redirect
// carried, exactly as the root does.
const visitOf = (page: Page, pending: boolean): Visit => ({
  pending,
  props: page.props,
  flash: (page.props.flash as Flash | undefined) ?? {},
  transient: page.transient,
  errors: {},
})

const entryOf = ({ name, baseUrl, props }: Page): Entry => ({
  name,
  url: `${baseUrl.pathname}${baseUrl.search}${baseUrl.hash}`,
  pathParams: (props.path_params as Record<string, string> | undefined) ?? {},
})

function createDriverLayer(
  initialPage: Page,
  enclosing: Host | null,
): DriverLayer {
  const page = writable(initialPage)
  const pending = writable(false)
  let current: FetchPageOperation | null = null
  let options: {
    fetchPage: FetchPage
    scope: Scope | undefined
    sink: PageSink | null
  } | null = null

  const receive: PageSink = (resolved) => {
    const { scope, sink } = options!
    if (scope && !scope(resolved)) {
      sink!(resolved)
    } else {
      page.set(resolved)
    }
  }

  const cancel = () => {
    if (!current) return
    current.cancelled = true
    current.abort()
  }

  // Settles once the resolved page has been received here or handed on; a
  // fetch cancelled by a later visit or by unmount has no driver waiting
  // for it, so its page reaches neither this driver nor its sink and the
  // promise never settles.
  const navigate = ({
    href,
    method = "get",
    data = {},
  }: VisitRequest): Promise<Visit> => {
    cancel()
    const next = options!.fetchPage({
      url: new URL(href, page.get().baseUrl),
      method: method as VisitMethod,
      data,
    })
    current = next
    pending.set(true)

    return new Promise<Visit>((resolve, reject) => {
      next.promise
        .then((resolved) => {
          if (next.cancelled) return

          pending.set(false)
          receive(resolved)
          resolve(visitOf(resolved, false))
        })
        .catch((error) => {
          if (next.cancelled) return

          pending.set(false)
          reject(error)
        })
    })
  }

  const host: Host = {
    session: enclosing?.session ?? writable(ANONYMOUS_SESSION),
    entry: derived([page], () => entryOf(page.get())),
    visit: derived([page, pending], () => visitOf(page.get(), pending.get())),
    navigate,
    guard: createLeaveGuard(),
    receive,
  }

  return {
    host,
    page,
    configure(next) {
      options = next
    },
    cancel,
  }
}
